"use client";

import React, {
  createContext,
  useContext,
  useEffect,
  useRef,
  useState,
} from "react";
import { useRouter } from "next/router";
import { track } from "../core/client";
import {
  getAndClearTraceId,
  generateViewId,
  setViewId,
  getViewId,
  clearViewId,
} from "../core/ids";
import { analyzePage, enrichEntityInfo } from "./page-map";
import { useSession } from "next-auth/react";

const __DEV__ = process.env.NODE_ENV !== "production";

// 전역(모듈) 단위 상태: 이펙트 재등록/리마운트 간에도 마지막 처리 경로를 보존
const __globalRouteState = {
  lastProcessedPath: "",
  isFirstSession: true, // 🆕 세션 첫 진입 플래그 (전역)
};

// 디버깅 스토어/유틸
const __now = () => Date.now();
function __getDebugStore() {
  if (!__DEV__) return null;
  if (typeof window === "undefined") return null;
  const w = window as unknown as any;
  if (!w.__analyticsDebug) {
    w.__analyticsDebug = {
      history: [] as any[],
      lastView: null as any,
      lastLeave: null as any,
      lastSetInfo: null as any,
      lastClearInfo: null as any,
      instances: new Set<string>(),
    };
  }
  return w.__analyticsDebug as {
    history: any[];
    lastView: any;
    lastLeave: any;
    lastSetInfo: any;
    lastClearInfo: any;
    instances: Set<string>;
  };
}

/**
 * 페이지 뷰 추적을 위한 Context와 컴포넌트
 */

interface ScreenContextValue {
  isTracking: boolean;
  currentPage: string | null;
}

const ScreenContext = createContext<ScreenContextValue>({
  isTracking: false,
  currentPage: null,
});

export const useScreenContext = () => useContext(ScreenContext);

/**
 * 페이지 추적 Provider
 */
export function AnalyticsProvider({ children }: { children: React.ReactNode }) {
  const [isTracking, setIsTracking] = useState(false);
  const [currentPage, setCurrentPage] = useState<string | null>(null);

  useEffect(() => {
    setIsTracking(true);
  }, []);

  return (
    <ScreenContext.Provider value={{ isTracking, currentPage }}>
      {children}
    </ScreenContext.Provider>
  );
}

/**
 * 페이지 추적 상태 관리
 */
interface PageTrackingState {
  startTime: number;
  scrollDepth: number;
  interactionCount: number;
  maxScrollY: number;
  isFirstView: boolean;
}

/**
 * 스크롤 깊이 계산
 */
function calculateScrollDepth(): number {
  if (typeof window === "undefined") return 0;

  const scrollTop = window.pageYOffset || document.documentElement.scrollTop;
  const documentHeight = Math.max(
    document.body.scrollHeight,
    document.body.offsetHeight,
    document.documentElement.clientHeight,
    document.documentElement.scrollHeight,
    document.documentElement.offsetHeight
  );
  const windowHeight = window.innerHeight;

  if (documentHeight <= windowHeight) return 100;

  return Math.round((scrollTop / (documentHeight - windowHeight)) * 100);
}

/**
 * [페이지 추적 훅]
 * 페이지 진입
 * - view_id 생성 및 설정
 * - page_view 이벤트 전송
 * 페이지 이탈
 * - view_id 해제
 * - 스크롤 추적
 * - 상호작용 추적
 * - page_leave 이벤트 전송
 */
export function useScreenTracking(options?: { initialized?: boolean }) {
  const initialized = options?.initialized ?? true;
  const router = useRouter();
  const { status } = useSession();
  const { isTracking } = useScreenContext();
  const trackingState = useRef<PageTrackingState | null>(null);
  const pageViewSeq = useRef(0);
  const routeEffectRegSeq = useRef(0);
  const lastProcessedPathRef = useRef<string>(
    __globalRouteState.lastProcessedPath
  );
  const instanceIdRef = useRef<string>(Math.random().toString(36).slice(2, 8));
  const lastSetInfoRef = useRef<{
    path: string;
    ts: number;
    source: string;
  } | null>(null);

  // 페이지 뷰 이벤트 전송
  const sendPageView = (
    pathname: string,
    search: string = "",
    source: "initial" | "routeChangeComplete" | "manual" | "unknown" = "unknown"
  ) => {
    if (__DEV__) {
      const seq = ++pageViewSeq.current;
      // 그룹 로깅으로 호출 단위 묶기
      // eslint-disable-next-line no-console
      // console.groupCollapsed(
      //   `[Analytics] page_view call #${seq} from=${source} path=${pathname} search=${search}`
      // );
      // eslint-disable-next-line no-console
      // console.log({ isTracking, pathname, search, source, status });
      // eslint-disable-next-line no-console
      // console.trace("[Analytics] page_view trace");
      // eslint-disable-next-line no-console
      // console.groupEnd();
    }

    // 🆕 view_id 생성 및 설정
    const viewId = generateViewId();
    setViewId(viewId);

    const pageInfo = analyzePage(pathname, search);
    const traceId = getAndClearTraceId(pathname);

    // 🔍 디버그: isFirstSession 상태 확인
    // if (__DEV__) {
    //   // eslint-disable-next-line no-console
    //   console.log(
    //     `[Analytics][DEBUG] sendPageView - isFirstSession BEFORE: ${__globalRouteState.isFirstSession}`,
    //     { pathname, source }
    //   );
    // }

    if (isTracking) {
      track("page_view", {
        page_category: pageInfo.category,
        page_subtype: pageInfo.subtype, // 🆕 추가
        route_template: pageInfo.route_template,
        entry: __globalRouteState.isFirstSession, // 🔧 전역 상태 사용
        entity: pageInfo.entity.kind !== "none" ? pageInfo.entity : undefined,
        trace_id: traceId || undefined,
        from_section_id: undefined, // 🆕 추가 (추후 구현 가능)
        path_params:
          Object.keys(pageInfo.path_params).length > 0
            ? pageInfo.path_params
            : undefined,
        query_slim:
          Object.keys(pageInfo.query_slim).length > 0
            ? pageInfo.query_slim
            : undefined,
      });
    } else if (__DEV__) {
      // // eslint-disable-next-line no-console
      // console.debug(
      //   "[Analytics][dbg] page_view skipped due to isTracking=false",
      //   {
      //     path: pathname,
      //     search,
      //   }
      // );
    }

    // 🔧 첫 세션 플래그 해제 (전역)
    __globalRouteState.isFirstSession = false;

    // 🔍 디버그: isFirstSession 상태 확인
    if (__DEV__) {
      // eslint-disable-next-line no-console
      // console.log(
      //   `[Analytics][DEBUG] sendPageView - isFirstSession AFTER: ${__globalRouteState.isFirstSession}`
      // );
    }

    // 추적 상태 초기화 (이전 페이지의 상태를 덮어씀)
    trackingState.current = {
      startTime: Date.now(),
      scrollDepth: 0,
      interactionCount: 0,
      maxScrollY: 0,
      isFirstView: traceId !== null,
    };
    if (__DEV__) {
      const dbg = __getDebugStore();
      lastSetInfoRef.current = { path: pathname, ts: __now(), source };
      if (dbg) {
        dbg.lastSetInfo = { ...lastSetInfoRef.current };
        dbg.lastView = { path: pathname, ts: __now(), source };
        dbg.history.push({
          t: __now(),
          type: "page_view_set_state",
          path: pathname,
          source,
          instanceId: instanceIdRef.current,
        });
      }

      // eslint-disable-next-line no-console
      // console.log("[Analytics][dbg] trackingState initialized", {
      //   instanceId: instanceIdRef.current,
      //   path: pathname,
      //   source,
      //   state: trackingState.current,
      // });
    }
  };

  // 페이지 이탈 이벤트 전송
  const sendPageLeave = (url: string, reason: string = "unknown") => {
    // 🔧 URL을 pathname과 search로 분리
    const questionIndex = url.indexOf("?");
    const pathname =
      questionIndex === -1
        ? url
        : questionIndex === 0
        ? "/"
        : url.substring(0, questionIndex);
    const search = questionIndex === -1 ? "" : url.substring(questionIndex);

    if (__DEV__) {
      // eslint-disable-next-line no-console
      // console.log("[Analytics] page_leave call url=", url, {
      //   pathname,
      //   search,
      //   isTracking,
      //   hasState: Boolean(trackingState.current),
      //   reason,
      // });
      const dbg = __getDebugStore();
      if (dbg) {
        dbg.lastLeave = { path: pathname, ts: __now(), reason };
        dbg.history.push({
          t: __now(),
          type: "page_leave_called",
          path: pathname,
          hasState: Boolean(trackingState.current),
          instanceId: instanceIdRef.current,
          reason,
        });
      }
      // eslint-disable-next-line no-console
      // console.log("[Analytics][dbg] leave precheck", {
      //   instanceId: instanceIdRef.current,
      //   hasState: Boolean(trackingState.current),
      //   lastSetInfo: lastSetInfoRef.current,
      //   reason,
      // });
    }
    if (!isTracking || !trackingState.current) {
      if (__DEV__) {
        const dbg = __getDebugStore();
        if (dbg) {
          dbg.history.push({
            t: __now(),
            type: "leave_early_return",
            path: pathname,
            isTracking,
            hasState: Boolean(trackingState.current),
            reason,
          });
        }
        // eslint-disable-next-line no-console
        // console.log("[Analytics][dbg] leave early return", {
        //   path: pathname,
        //   isTracking,
        //   hasState: Boolean(trackingState.current),
        //   reason,
        // });
      }
      return;
    }

    const pageInfo = analyzePage(pathname, search);
    const stayMs = Date.now() - trackingState.current.startTime;
    const scrollDepth = Math.max(
      trackingState.current.scrollDepth,
      calculateScrollDepth()
    );

    track("page_leave", {
      page_category: pageInfo.category,
      page_subtype: pageInfo.subtype, // 🆕 추가
      route_template: pageInfo.route_template,
      stay_ms: stayMs,
      scroll_depth_pct: scrollDepth > 0 ? scrollDepth : undefined,
      interaction_cnt:
        trackingState.current.interactionCount > 0
          ? trackingState.current.interactionCount
          : undefined,
    });

    // 🆕 view_id 해제
    clearViewId();

    // ⚠️ trackingState.current = null 제거!
    // 이유: sendPageView에서 어차피 덮어쓰므로 불필요
    // 오히려 비동기 타이밍 문제로 인해 다음 페이지의 page_leave가 안 찍히는 버그 발생
    // trackingState.current = null;

    if (__DEV__) {
      const dbg = __getDebugStore();
      if (dbg) {
        dbg.lastClearInfo = { path: pathname, ts: __now(), reason };
        dbg.history.push({
          t: __now(),
          type: "leave_completed_state_kept", // 🆕 이름 변경
          reason,
          path: pathname,
        });
      }
    }
  };

  // 스크롤 추적 (scrollDepth)
  useEffect(() => {
    if (!isTracking) return;

    let ticking = false;

    const handleScroll = () => {
      if (!ticking && trackingState.current) {
        requestAnimationFrame(() => {
          if (trackingState.current) {
            const depth = calculateScrollDepth();
            trackingState.current.scrollDepth = Math.max(
              trackingState.current.scrollDepth,
              depth
            );
          }
          ticking = false;
        });
        ticking = true;
      }
    };

    window.addEventListener("scroll", handleScroll, { passive: true });
    return () => window.removeEventListener("scroll", handleScroll);
  }, [isTracking]);

  // 상호작용 추적
  useEffect(() => {
    if (!isTracking) return;

    const handleInteraction = () => {
      if (trackingState.current) {
        trackingState.current.interactionCount++;
      }
    };

    // 가벼운 상호작용들 추적
    const events = ["click", "keydown", "touchstart"];
    events.forEach((event) => {
      document.addEventListener(event, handleInteraction, { passive: true });
    });

    return () => {
      events.forEach((event) => {
        document.removeEventListener(event, handleInteraction);
      });
    };
  }, [isTracking]);

  // 라우터 이벤트 리스너 - page_view
  useEffect(() => {
    if (!router || !isTracking || status === "loading" || !initialized) return;

    // 마지막으로 처리한 pathname을 ref로 유지 (이펙트 재등록 시에도 유지)
    const regId = ++routeEffectRegSeq.current;
    if (__DEV__) {
      // eslint-disable-next-line no-console
      // console.debug(`[Analytics] register route listeners #${regId}`, {
      //   isTracking,
      //   status,
      //   isReady: router.isReady,
      //   asPath: router.asPath,
      // });
    }

    // 현재 페이지 이탈 이벤트
    const handleRouteChangeStart = (url: string) => {
      // 🔧 pathname만 추출해서 비교 (쿼리스트링 변경은 무시)
      const [nextPathname] = url.split("?");
      // 🔧 locale 제거 후 빈 문자열이면 "/" 사용
      const normalizedNextPath =
        nextPathname.replace(/^\/[a-z]{2}(?=\/|$)/, "") || "/";
      const currentPath = __globalRouteState.lastProcessedPath;
      if (__DEV__) {
        // console.debug(`[Analytics] routeChangeStart (#${regId}) url=`, url, {
        //   ts: __now(),
        //   normalizedNextPath,
        //   currentPath,
        //   lastSetInfo: lastSetInfoRef.current,
        // });
      }

      // 🔧 pathname이 실제로 변경되는 경우에만 page_leave 전송
      if (normalizedNextPath !== currentPath) {
        sendPageLeave(router.asPath, "routeChangeStart");
      } else if (__DEV__) {
        // console.debug(
        //   `[Analytics] routeChangeStart (#${regId}) skipped (same pathname, query changed only)`,
        //   { normalizedNextPath, currentPath }
        // );
      }
    };

    // 새 페이지 뷰 이벤트
    const handleRouteChangeComplete = (url: string) => {
      // 🔧 URL 파싱 개선: pathname이 없으면 '/'로 처리
      const questionIndex = url.indexOf("?");
      let pathname: string;
      let search: string;

      if (questionIndex === -1) {
        // 쿼리스트링 없음
        pathname = url;
        search = "";
      } else if (questionIndex === 0) {
        // 🔧 "?type=my" 형태 (pathname 없음) → "/" 로 처리
        pathname = "/";
        search = url.substring(1);
      } else {
        // 일반적인 경우 "/path?query"
        pathname = url.substring(0, questionIndex);
        search = url.substring(questionIndex + 1);
      }

      // locale이 제거된 실제 pathname 구하기
      const normalizedPath = pathname.replace(/^\/[a-z]{2}(?=\/|$)/, "") || "/";

      if (__DEV__) {
        console.debug(`[Analytics][dbg] routeChangeComplete normalize`, {
          url,
          pathname,
          normalizedPath,
          search,
          prevProcessed: lastProcessedPathRef.current,
          ts: __now(),
        });
      }

      // 🔧 pathname이 실제로 변경되는 경우에만 처리 (쿼리만 변경은 무시)

      if (__globalRouteState.lastProcessedPath === normalizedPath) {
        if (__DEV__) {
          console.debug(
            `[Analytics] routeChangeComplete (#${regId}) skipped (same pathname)`,
            {
              url,
              normalizedPath,
              lastProcessedPath: __globalRouteState.lastProcessedPath,
              hasTrackingState: Boolean(trackingState.current),
            }
          );
        }

        // 🆕 trackingState가 없으면 초기화 (page_leave를 위해 필요)
        if (!trackingState.current) {
          console.warn(
            `[Analytics] trackingState is null despite being on same page. Initializing...`,
            { path: normalizedPath }
          );
          trackingState.current = {
            startTime: Date.now(),
            scrollDepth: 0,
            interactionCount: 0,
            maxScrollY: 0,
            isFirstView: false,
          };
        }

        return;
      }

      lastProcessedPathRef.current = normalizedPath;
      __globalRouteState.lastProcessedPath = normalizedPath;
      if (__DEV__) {
        console.debug(
          `[Analytics] routeChangeComplete (#${regId}) -> page_view`,
          { url, normalizedPath, search }
        );
      }
      sendPageView(
        normalizedPath,
        search ? `?${search}` : "",
        "routeChangeComplete"
      );
    };

    // 초기 페이지 뷰 (첫 로드 포함): routeChangeComplete와 동일 로직으로 1회만 처리
    if (router.isReady) {
      // setTimeout으로 이벤트 순서 경쟁을 완화
      setTimeout(() => {
        handleRouteChangeComplete(router.asPath);

        // 🆕 안전장치: trackingState가 여전히 null이면 강제 초기화
        if (!trackingState.current) {
          console.warn(
            `[Analytics] trackingState still null after initial routeChangeComplete. Force initializing...`,
            { path: router.asPath }
          );
          trackingState.current = {
            startTime: Date.now(),
            scrollDepth: 0,
            interactionCount: 0,
            maxScrollY: 0,
            isFirstView: false,
          };
        }
      }, 0);
    }

    router.events.on("routeChangeStart", handleRouteChangeStart);
    router.events.on("routeChangeComplete", handleRouteChangeComplete);

    return () => {
      router.events.off("routeChangeStart", handleRouteChangeStart);
      router.events.off("routeChangeComplete", handleRouteChangeComplete);
      if (__DEV__) {
        console.debug(`[Analytics] cleanup route listeners #${regId}`);
      }
    };
  }, [router, isTracking, status, initialized]);

  // 디펜던시 변화 디버그
  useEffect(() => {
    if (!__DEV__) return;
    // eslint-disable-next-line no-console
    // console.debug("[Analytics] deps changed", {
    //   isTracking,
    //   status,
    //   routerReady: router?.isReady,
    //   asPath: router?.asPath,
    // });
  }, [isTracking, status, router?.isReady]);

  // 페이지 언로드 시 이탈 이벤트
  useEffect(() => {
    if (!isTracking) return;

    const handleBeforeUnload = () => {
      if (router) {
        sendPageLeave(router.asPath, "beforeunload");
      }
    };

    const handleVisibilityChange = () => {
      if (document.visibilityState === "hidden" && router) {
        sendPageLeave(router.asPath, "visibility_hidden");
      }
    };

    window.addEventListener("beforeunload", handleBeforeUnload);
    document.addEventListener("visibilitychange", handleVisibilityChange);

    return () => {
      window.removeEventListener("beforeunload", handleBeforeUnload);
      document.removeEventListener("visibilitychange", handleVisibilityChange);
    };
  }, [router, isTracking]);
}

/**
 * 화면 추적 컴포넌트 (자동 설정)
 */
export function ScreenTracking({ initialized }: { initialized?: boolean }) {
  useScreenTracking({ initialized });
  return null;
}

/**
 * 수동 페이지 뷰 추적 (SPA에서 특별한 경우)
 */
export function trackPageView(
  pathname: string,
  search: string = "",
  additionalProps: any = {}
) {
  const pageInfo = analyzePage(pathname, search);

  track("page_view", {
    page_category: pageInfo.category,
    page_subtype: pageInfo.subtype, // 🆕 추가
    route_template: pageInfo.route_template,
    entity:
      pageInfo.entity.kind !== "none"
        ? enrichEntityInfo(pageInfo.entity, additionalProps.entity)
        : undefined,
    path_params:
      Object.keys(pageInfo.path_params).length > 0
        ? pageInfo.path_params
        : undefined,
    query_slim:
      Object.keys(pageInfo.query_slim).length > 0
        ? pageInfo.query_slim
        : undefined,
    ...additionalProps,
  });
}

/**
 * 수동 페이지 이탈 추적
 */
export function trackPageLeave(
  pathname: string,
  stayMs: number,
  additionalProps: any = {}
) {
  const pageInfo = analyzePage(pathname);

  track("page_leave", {
    page_category: pageInfo.category,
    page_subtype: pageInfo.subtype, // 🆕 추가
    route_template: pageInfo.route_template,
    stay_ms: stayMs,
    ...additionalProps,
  });
}

/**
 * 현재 페이지 정보 반환 훅
 */
export function useCurrentPage() {
  const router = useRouter();
  const [pageInfo, setPageInfo] = useState<ReturnType<
    typeof analyzePage
  > | null>(null);

  useEffect(() => {
    if (router.isReady) {
      const [pathname, search] = router.asPath.split("?");
      setPageInfo(analyzePage(pathname, search ? `?${search}` : ""));
    }
  }, [router.asPath, router.isReady]);

  return pageInfo;
}

/**
 * 체류 시간 추적 훅
 */
export function useStayTime() {
  const [startTime] = useState(() => Date.now());
  const [stayTime, setStayTime] = useState(0);

  useEffect(() => {
    const interval = setInterval(() => {
      setStayTime(Date.now() - startTime);
    }, 1000);

    return () => clearInterval(interval);
  }, [startTime]);

  return {
    stayTime,
    stayTimeSeconds: Math.floor(stayTime / 1000),
    stayTimeMinutes: Math.floor(stayTime / 60000),
  };
}

/**
 * 스크롤 깊이 추적 훅
 */
export function useScrollDepth() {
  const [scrollDepth, setScrollDepth] = useState(0);
  const [maxScrollDepth, setMaxScrollDepth] = useState(0);

  useEffect(() => {
    let ticking = false;

    const handleScroll = () => {
      if (!ticking) {
        requestAnimationFrame(() => {
          const depth = calculateScrollDepth();
          setScrollDepth(depth);
          setMaxScrollDepth((prev) => Math.max(prev, depth));
          ticking = false;
        });
        ticking = true;
      }
    };

    window.addEventListener("scroll", handleScroll, { passive: true });
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  return {
    scrollDepth,
    maxScrollDepth,
    scrollPercentage: scrollDepth,
  };
}
